<!-- 巡检任务详情 -->
<template>
  <div class="inspection-task-detail">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <el-button
          type="text"
          icon="el-icon-arrow-left"
          @click="handleBack"
          class="back-btn"
          >返回</el-button
        >
        <h1 class="page-title">巡检任务详情</h1>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 基础信息区域 -->
      <div class="basic-info-section">
        <div class="basic-info-header">
          <h3>基础信息</h3>
          <el-tag
            :type="getStatusType(taskDetail.orderStatus)"
            size="large"
            class="status-tag"
          >
            {{ getStatusText(taskDetail.orderStatus) }}
          </el-tag>
        </div>

        <div class="basic-info-content">
          <!-- 基础信息网格 -->
          <div class="info-grid">
            <div class="info-item">
              <span class="label">工单编号:</span>
              <span class="value link">{{ taskDetail.workOrderId }}</span>
            </div>
            <div class="info-item">
              <span class="label">计划名称:</span>
              <span class="value">{{ taskDetail.extPlanName }}</span>
            </div>
            <div class="info-item">
              <span class="label">执行周期:</span>
              <span class="value">{{
                getCycleText(taskDetail.executeCycle)
              }}</span>
            </div>
            <div class="info-item">
              <span class="label">计划时间:</span>
              <span class="value">{{ getPlanTimeText() }}</span>
            </div>
            <div class="info-item">
              <span class="label">巡检路线:</span>
              <span class="value">{{ taskDetail.inspectionRouteName }}</span>
            </div>
            <div class="info-item">
              <span class="label">创建时间:</span>
              <span class="value">{{
                formatDateTime(taskDetail.createTime)
              }}</span>
            </div>
            <div class="info-item">
              <span class="label">巡检人员:</span>
              <span class="value">{{ taskDetail.planPersonNames }}</span>
            </div>
            <div class="info-item">
              <span class="label">实际开始时间:</span>
              <span class="value">{{
                formatDateTime(taskDetail.actualStartTime)
              }}</span>
            </div>
            <div class="info-item">
              <span class="label">实际结束时间:</span>
              <span class="value">{{
                formatDateTime(taskDetail.actualEndTime)
              }}</span>
            </div>
            <div class="info-item">
              <span class="label">预计耗时:</span>
              <span class="value">{{
                taskDetail.estimatedTime || "30分钟"
              }}</span>
            </div>
            <div class="info-item">
              <span class="label">审核状态:</span>
              <span class="value">{{ getAuditStatusText(taskDetail.auditStatus) }}</span>
            </div>
            <div class="info-item">
              <span class="label">审核原因:</span>
              <span class="value">{{ taskDetail.auditReason || "--" }}</span>
            </div>
          </div>

          <!-- 任务进度区域 -->
          <div class="task-progress-section">
            <h3>任务进度</h3>
            <div class="progress-info">
              <div class="time-info">
                <span class="label">总体耗时:</span>
                <span class="value">{{
                  taskDetail.totalTimes
                }}</span>
              </div>
              <div class="progress-bar-section">
                <span class="label">总体进度</span>
                <el-progress
                  :percentage="taskDetail.progressPercentage"
                  :color="getProgressColor(taskDetail.progressPercentage)"
                  :stroke-width="8"
                />
              </div>
              <div class="stats-container">
                <div class="stat-item">
                  <span class="stat-number">{{
                    taskDetail.planPointCount || 0
                  }}</span>
                  <span class="stat-label">计划点位</span>
                </div>
                <div class="stat-item">
                  <span class="stat-number completed">{{
                    taskDetail.completedPointCount || 0
                  }}</span>
                  <span class="stat-label">已完成</span>
                </div>
                <div class="stat-item">
                  <span class="stat-number abnormal">{{
                    taskDetail.exceptionPointCount || 0
                  }}</span>
                  <span class="stat-label">异常</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 点位详情 -->
    <div class="point-details-section">
      <h3>点位详情</h3>
      <div class="point-card-list">
        <div
          v-for="(point, pointIndex) in taskDetail.pointDetails || []"
          :key="point.id || point.pointCode || pointIndex"
          class="point-card"
        >
          <div class="point-card__header">
            <div class="point-card__left">
              <span class="point-card__badge" aria-hidden="true">
                <i class="el-icon-location-outline"></i>
              </span>
              <span class="point-card__order">{{ chineseOrderNumeral(pointIndex + 1) }}、</span>
              <span class="point-card__title">巡检点位：</span>
              <span class="point-card__name">{{ point.pointName || "--" }}</span>
            </div>
          </div>
          <div class="point-card__meta-grid">
            <div class="meta-item"><span class="meta-label">点位编号</span><span class="meta-value">{{ point.pointCode || "--" }}</span></div>
            <div class="meta-item"><span class="meta-label">点位位置</span><span class="meta-value">{{ point.pointLocation || "--" }}</span></div>
            <div class="meta-item"><span class="meta-label">巡检工程师</span><span class="meta-value">{{ point.responsibleEngineerName || "--" }}</span></div>
          </div>
          <div class="point-card__items">
            <div class="point-card__items-title">巡检项检查情况</div>
            <div v-if="getDisplayItems(point).length" class="point-item-list">
              <el-table
                :data="getDisplayItems(point)"
                size="mini"
                border
                stripe
                class="point-item-inner-table"
                :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
              >
                <el-table-column label="巡检项" min-width="220">
                  <template slot-scope="{ row, $index }">
                    <span class="item-name">{{ $index + 1 }}. {{ row.associatedObjectName || "--" }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="检查时间" min-width="160">
                  <template slot-scope="{ row }">
                    <span>{{ formatDateTime(row.processTime) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="检查状态" width="100" align="center">
                  <template slot-scope="{ row }">
                    <el-tag :type="getCheckStateType(row)" size="mini">
                      {{ getCheckStateText(row) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="巡检备注" min-width="180" show-overflow-tooltip>
                  <template slot-scope="{ row }">
                    <span>{{ row.remark || "--" }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="100" align="center">
                  <template slot-scope="{ row }">
                    <el-button type="text" @click="handleViewImageAction(row)">查看图片</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
            <div v-else class="point-items-empty">暂无巡检项</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 巡检轨迹 -->
    <div class="inspection-track-section">
      <h3>巡检轨迹</h3>
      <p v-if="hasInspectionMapCoords" class="map-hint">
        按表格顺序连线；标记颜色：
        <span class="legend legend--ok">已巡检正常</span>
        <span class="legend legend--bad">异常</span>
        <span class="legend legend--pending">未巡检</span>
      </p>
      <div class="map-container">
        <div
          v-if="!loading && !hasInspectionMapCoords"
          class="map-placeholder"
        >
          <i class="el-icon-location"></i>
          <p>暂无点位经纬度，无法展示地图</p>
          <p class="map-placeholder-sub">请确认巡检点位已维护坐标（经度/纬度）</p>
        </div>
        <TiandiMap
          v-if="hasInspectionMapCoords"
          class="inspection-map-el"
          height="100%"
          :path-points="inspectionPathPoints"
          :auto-fit="true"
          :initial-markers="[]"
          :show-toolbar="false"
        ></TiandiMap>
      </div>
    </div>

    <!-- 图片查看对话框 -->
    <el-dialog
      title="查看图片"
      :visible.sync="imageDialogVisible"
      width="60%"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :modal="true"
      :append-to-body="true"
      :z-index="3000"
      center
      class="image-dialog"
    >
      <div class="image-detail-content">
        <!-- 异常状态 -->
        <div class="status-section">
          <div class="status-item">
            <i class="el-icon-warning status-icon"></i>
            <span class="status-text">
              {{ getPointStatusText(currentImageData.status) }}</span
            >
          </div>
        </div>

        <!-- 问题描述 -->
        <div class="description-section">
          <h4 class="section-title">问题描述</h4>
          <div class="description-content">
            <p class="description-text">
              {{ currentImageData.imageDesc || "暂无描述" }}
            </p>
            <div class="report-time">
              上报时间: {{ currentImageData.createTime }}
            </div>
          </div>
        </div>

        <!-- 现场照片 -->
        <div class="photos-section">
          <h4 class="section-title">现场照片</h4>
          <div class="photos-grid">
            <div
              v-for="(image, index) in currentImageList"
              :key="index"
              class="photo-item"
            >
              <div class="photo-container">
                <img
                  :src="image.url"
                  :alt="image.desc"
                  @error="handleImageError"
                  style="width: 100%;"
                  class="photo-image"
                />
                <div class="photo-timestamp">{{ image.timestamp }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 巡检备注 -->
        <div class="remark-section">
          <h4 class="section-title">巡检备注</h4>
          <div class="remark-content">
            <p class="remark-text">
              {{ currentImageData.remark || "暂无备注" }}
            </p>
            <div class="remark-info">
              填写人: {{ currentImageData.responsibleEngineerName }} | 填写时间:
              {{ currentImageData.updateTime }}
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getDetail } from "./api";
import TiandiMap from "@/components/tiandi-map/index.vue";

export default {
  name: "InspectionTaskDetail",
  components: {
    TiandiMap,
  },
  data() {
    return {
      taskDetail: {
        workOrderId: "",
        extPlanName: "",
        inspectionRouteName: "",
        orderStatus: 1,
        totalTime: "",
        totalTimes: "", // 总体耗时
        progressPercentage: 0,
        planPointCount: 0,
        completedPointCount: 0,
        abnormalCount: 0,
        actualStartTime: "",
        executeCycle: 1,
        createTime: "",
        actualEndTime: "",
        cycleStartTime: "",
        cycleEndTime: "",
        planPersonNames: "",
        estimatedTime: "",
        auditStatus: 0,
        auditReason: "",
        pointDetails: [],
      },
      loading: false,
      imageDialogVisible: false,
      currentImageList: [],
      currentImageData: {},
    };
  },
  computed: {
    /** 含有效经纬度的点位片段（顺序与表格一致） */
    inspectionMapSegments() {
      const list = this.taskDetail.pointDetails || [];
      const segments = [];
      for (let i = 0; i < list.length; i++) {
        const p = list[i];
        const lng = p.longitude != null ? p.longitude : p.lng;
        const lat = p.latitude != null ? p.latitude : p.lat;
        if (lng == null || lat == null) continue;
        const lngN = Number(lng);
        const latN = Number(lat);
        if (Number.isNaN(lngN) || Number.isNaN(latN)) continue;
        segments.push({
          position: [lngN, latN],
          point: p,
          order: i + 1,
        });
      }
      return segments;
    },
    hasInspectionMapCoords() {
      return this.inspectionMapSegments.length > 0;
    },
    inspectionPathPoints() {
      return this.inspectionMapSegments.map((item) => ({
        coordinate: item.position,
        name: item.point.pointName || String(item.order),
      }));
    },
  },
  mounted() {
    this.loadTaskDetail();
  },
  beforeDestroy() {
  },
  methods: {
    getMarkerStrokeColor(status) {
      if (status === "normal") return "#67c23a";
      if (status === "abnormal") return "#f56c6c";
      return "#909399";
    },

    // 更新总体耗时
    updateTotalTimes() {
      if (this.taskDetail.orderStatus === 1 && this.taskDetail.actualStartTime) {
        const startTime = new Date(this.taskDetail.actualStartTime).getTime();
        const currentTime = new Date().getTime();
        const durationMs = currentTime - startTime;
        this.taskDetail.totalTimes = this.formatDuration(durationMs);
      }
    },

    // 将毫秒格式化为时分秒
    formatDuration(ms) {
      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      // 格式化为时分秒，补零
      const formattedHours = hours.toString().padStart(2, '0');
      const formattedMinutes = minutes.toString().padStart(2, '0');
      const formattedSeconds = seconds.toString().padStart(2, '0');
      if (formattedHours > 0) {
        return `${formattedHours}时${formattedMinutes}分${formattedSeconds}秒`;
      } else if (formattedMinutes > 0) {
        return `${formattedMinutes}分${formattedSeconds}秒`;
      } else {
        return `${formattedSeconds}秒`;
      }
    },

    // 加载任务详情
    async loadTaskDetail() {
      try {
        this.loading = true;
        const taskId = this.$route.params.id;
        const response = await getDetail(taskId);
        if (response && response.data) {
          const data = response.data.data;
          this.taskDetail = {
            ...data,
            progressPercentage: this.calculateProgress(
              data.completedPointCount,
              data.planPointCount
            ),
            estimatedTime: data.estimatedTime
              ? `${data.estimatedTime}分钟`
              : "",
            pointDetails: this.mapPointDetails(data.childrenList || []),
          };

          // 如果是执行中状态，初始化总体耗时
          if (this.taskDetail.orderStatus === 1 && this.taskDetail.actualStartTime) {
            this.updateTotalTimes();
          }

        }
      } catch (error) {
        console.error("加载任务详情失败:", error);
        this.$message.error("加载任务详情失败");
      } finally {
        this.loading = false;
      }
    },
    // 计算进度百分比
    calculateProgress(completed, total) {
      if (!total || total === 0) return 0;
      return Math.round((completed / total) * 100);
    },
    // 映射点位详情数据
    mapPointDetails(childrenList) {
      return childrenList.map((item) => ({
        id: item.id,
        pointCode: item.pointCode || "",
        pointName: item.pointName || "",
        pointLocation: item.pointLocation || "",
        responsibleEngineerName: item.responsibleEngineerName || "",
        createTime: item.createTime || "",
        updateTime: item.updateTime || "",
        updateName: item.updateName || "",
        actualInspectionTime: item.processTime || "",
        status: item.status === 1 ? "normal" : (item.status === 2 ? "other" : "abnormal"),
        imageUrls: item.imageUrls || "",
        imageDesc: item.imageDesc || "",
        remark: item.remark || "",
        associatedObjectName: item.associatedObjectName || "",
        items: this.mapPointItems(item.inspectionWorkOrderItemRpcDtoList || []),
      }));
    },
    mapPointItems(itemList) {
      return itemList.map((item) => ({
        id: item.id,
        associatedObjectName: item.associatedObjectName || "",
        orderStatus: item.orderStatus,
        /** 检查状态：2未巡检 0异常 1正常（空视为未巡检） */
        status: item.status != null ? item.status : null,
        imageUrls: item.imageUrls || "",
        imageDesc: item.imageDesc || "",
        remark: item.remark || "",
        processTime: item.processTime || "",
        createTime: item.createTime || "",
        updateTime: item.updateTime || "",
        responsibleEngineerName: item.updateName || "",
        longitude: item.longitude,
        latitude: item.latitude,
        lng: item.lng,
        lat: item.lat,
      }));
    },
    // 获取状态类型
    getStatusType(status) {
      const typeMap = {
        0: "info",
        1: "primary",
        2: "success",
        3: "danger",
      };
      return typeMap[status] || "info";
    },

    // 获取状态文本
    getStatusText(status) {
      const textMap = {
        0: "待执行",
        1: "执行中",
        2: "已完成",
        3: "已超时",
      };
      return textMap[status] || "未知";
    },

    getAuditStatusText(status) {
      const textMap = {
        0: "未审核",
        1: "审核通过",
        2: "已驳回",
      };
      return textMap[status == null ? 0 : Number(status)] || "未审核";
    },

    // 获取进度条颜色
    getProgressColor(percentage) {
      if (percentage >= 100) return "#67c23a";
      if (percentage >= 75) return "#409eff";
      if (percentage >= 50) return "#e6a23c";
      return "#f56c6c";
    },

    // 获取周期文本
    getCycleText(cycle) {
      const cycleMap = {
        1: "每天",
        2: "每周",
        3: "每月",
        4: "自定义",
      };
      return cycleMap[cycle] || "未知";
    },

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return "--";
      // 如果是时间戳，转换为日期对象
      if (typeof dateTime === "number") {
        return new Date(dateTime).toLocaleString("zh-CN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      }
      // 如果是字符串，直接返回
      return dateTime;
    },

    // 获取计划时间文本
    getPlanTimeText() {
      const startTime = this.formatDateTime(this.taskDetail.cycleStartTime);
      const endTime = this.formatDateTime(this.taskDetail.cycleEndTime);
      return `开始${startTime} 结束${endTime}`;
    },

    // 获取点位状态类型
    getPointStatusType(status) {
      return status === "normal" ? "success" : "danger";
    },

    // 获取点位状态文本
    getPointStatusText(status) {
      if (status === "normal") {
        return "正常";
      } else if (status === "abnormal") {
        return "异常";
      } else if (status === "other") {
        return "未巡检";
      } else {
        return "未知状态";
      }
    },
    /** 检查状态：未巡检(status空或2)、异常(0)、正常(1) */
    getCheckStateText(row) {
      if (row == null) return "--";
      const s = row.status;
      if (s === null || s === undefined) return "未巡检";
      const n = Number(s);
      if (n === 2) return "未巡检";
      if (n === 0) return "异常";
      if (n === 1) return "正常";
      return "--";
    },
    getCheckStateType(row) {
      if (row == null) return "info";
      const s = row.status;
      if (s === null || s === undefined) return "info";
      const n = Number(s);
      if (n === 2) return "info";
      if (n === 0) return "danger";
      if (n === 1) return "success";
      return "info";
    },
    chineseOrderNumeral(num) {
      const n = Number(num);
      if (!n || n < 1) return "一";
      const d = "零一二三四五六七八九";
      if (n <= 10) return n === 10 ? "十" : d[n];
      if (n < 20) return "十" + (n % 10 ? d[n % 10] : "");
      if (n < 100) {
        const tens = Math.floor(n / 10);
        const ones = n % 10;
        return d[tens] + "十" + (ones ? d[ones] : "");
      }
      return String(n);
    },

    // 查看图片
    handleViewImage(row) {
      console.log("查看图片:", row);

      // 保存当前行数据
      this.currentImageData = row;

      // 解析图片URLs
      const imageUrls = row.imageUrls ? row.imageUrls.split(",") : [];
      const imageList = imageUrls
        .filter((url) => url.trim() !== "") // 过滤空URL
        .map((url, index) => {
          let fullUrl = url.trim();
          // 如果URL不是完整路径，添加基础URL
          if (fullUrl && !fullUrl.startsWith("http")) {
            // 这里可以根据实际情况配置基础URL
            fullUrl = fullUrl.startsWith("/") ? fullUrl : "/" + fullUrl;
          }
          return {
            url: fullUrl,
            desc: row.imageDesc || `图片${index + 1}`,
            timestamp: this.formatTimeFromProcessTime(row.processTime),
          };
        })
        .filter((item) => this.isValidImageUrl(item.url)); // 过滤无效的图片URL

      this.currentImageList = imageList;
      this.imageDialogVisible = true;
    },

    // 检查图片URL是否有效
    isValidImageUrl(url) {
      if (!url || url.trim() === "") return false;
      // 检查是否是有效的图片URL
      const imageExtensions = [
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".bmp",
        ".webp",
      ];
      const lowerUrl = url.toLowerCase();
      return (
        imageExtensions.some((ext) => lowerUrl.includes(ext)) ||
        url.startsWith("data:image")
      );
    },

    // 从processTime格式化时间戳
    formatTimeFromProcessTime(processTime) {
      if (!processTime) return "";
      const date = new Date(processTime);
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    },
    getImageCount(imageUrls) {
      if (!imageUrls) return 0;
      return String(imageUrls)
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean).length;
    },
    handleViewImageAction(row) {
      if (!row || !row.imageUrls) {
        this.$message.warning("该巡检项暂无图片");
        return;
      }
      this.handleViewImage(row);
    },
    getDisplayItems(point) {
      if (point && Array.isArray(point.items) && point.items.length) {
        return point.items;
      }
      // 兜底：后端未返回巡检项列表时，按点位上的巡检项名称拆分展示
      if (point && point.associatedObjectName) {
        return String(point.associatedObjectName)
          .split(/[,，、]/)
          .map((s) => s.trim())
          .filter(Boolean)
          .map((name) => ({
            associatedObjectName: name,
            orderStatus: null,
            status: null,
            imageUrls: "",
            imageDesc: "",
            remark: "",
            processTime: "",
          }));
      }
      return [];
    },

    // 处理图片加载错误
    handleImageError(event) {
      console.error("图片加载失败:", event.target.src);
      // 避免无限循环加载占位图
      if (
        !event.target.src.includes("data:image") &&
        !event.target.src.includes("placeholder")
      ) {
        // 使用base64编码的简单占位图，避免网络请求
        const placeholderImage =
          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDIwMCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTUwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik04MCA2MEgxMjBWOTBIODBWNjBaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik05MCA3MEgxMTBWODBIOTBWNzBaIiBmaWxsPSIjOTk5OTk5Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTIwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOTk5OTk5IiBmb250LXNpemU9IjEyIj7lm77niYfliqDovb3lpLHotKU8L3RleHQ+Cjwvc3ZnPgo=";
        event.target.src = placeholderImage;
      } else {
        // 如果占位图也加载失败，显示默认文本
        event.target.style.display = "none";
        const parent = event.target.parentElement;
        if (parent && !parent.querySelector(".image-error-text")) {
          const errorText = document.createElement("div");
          errorText.className = "image-error-text";
          errorText.textContent = "图片加载失败";
          errorText.style.cssText = `
            width: 100%;
            height: 150px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f5f5f5;
            color: #999;
            font-size: 14px;
            border: 1px dashed #ddd;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          `;
          parent.appendChild(errorText);
        }
      }
    },

    // 返回
    handleBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
.inspection-task-detail {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .header-left {
      display: flex;
      align-items: center;
      gap: 15px;

      .back-btn {
        font-size: 16px;
        color: #409eff;
        padding: 0;

        &:hover {
          color: #66b1ff;
        }
      }

      .page-title {
        margin: 0;
        font-size: 24px;
        font-weight: 600;
        color: #333;
      }
    }
  }

  .main-content {
    margin-bottom: 20px;
    width: 100%;
  }

  .task-status-section {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .status-tag {
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    .progress-card {
      background: white;
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

      h3 {
        margin: 0 0 15px 0;
        font-size: 16px;
        color: #333;
      }

      .progress-info {
        .time-info {
          margin-bottom: 20px;
          padding: 10px 14px;
          background: #f0f7ff;
          border-radius: 6px;
          border-left: 3px solid #409eff;

          .label {
            color: #666;
            margin-right: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .value {
            color: #333;
            font-weight: 600;
            font-size: 15px;
          }
        }

        .progress-bar-section {
          margin-bottom: 20px;

          .label {
            display: block;
            color: #666;
            margin-bottom: 12px;
            font-size: 14px;
            font-weight: 500;
          }

          ::v-deep .el-progress {
            .el-progress-bar__outer {
              background-color: #f0f2f5;
              border-radius: 10px;
              height: 12px;
            }

            .el-progress-bar__inner {
              border-radius: 10px;
              transition: width 0.6s ease;
            }

            .el-progress__text {
              font-size: 14px;
              font-weight: 600;
              color: #333;
            }
          }
        }

        .stats-container {
          display: flex;
          gap: 20px;
          margin-top: 20px;

          .stat-item {
            text-align: center;
            padding: 12px 16px;
            background: #f8f9fa;
            border-radius: 6px;
            flex: 1;
            min-width: 80px;

            .stat-number {
              display: block;
              font-size: 24px;
              font-weight: 700;
              color: #333;
              margin-bottom: 6px;
              line-height: 1;

              &.completed {
                color: #67c23a;
              }

              &.abnormal {
                color: #f56c6c;
              }
            }

            .stat-label {
              font-size: 13px;
              color: #666;
              font-weight: 500;
            }
          }
        }
      }
    }
  }

  .basic-info-section {
    background: white;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    width: 100%;
    height: fit-content;
    overflow: visible;

    .basic-info-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      h3 {
        margin: 0;
        font-size: 18px;
        color: #333;
        font-weight: 600;
        padding-bottom: 12px;
        flex: 1;
      }

      .status-tag {
        margin-left: 20px;
      }
    }

    .basic-info-content {
      display: flex;
      gap: 20px;
      align-items: flex-start;
    }
  }

  .task-progress-section {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid #e8e8e8;
    flex: 0 0 auto;
    width: 300px;
    height: fit-content;
    overflow: visible;

    h3 {
      margin: 0 0 16px 0;
      font-size: 16px;
      color: #333;
      font-weight: 600;
      padding-bottom: 8px;
    }

    .progress-info {
      .time-info {
        margin-bottom: 16px;
        padding: 10px 14px;
        background: #f0f7ff;
        border-radius: 6px;
        border-left: 3px solid #409eff;

        .label {
          color: #666;
          margin-right: 8px;
          font-size: 13px;
          font-weight: 500;
        }

        .value {
          color: #333;
          font-weight: 700;
          font-size: 15px;
        }
      }

      .progress-bar-section {
        margin-bottom: 16px;

        .label {
          display: block;
          color: #666;
          margin-bottom: 10px;
          font-size: 13px;
          font-weight: 500;
        }

        ::v-deep .el-progress {
          .el-progress-bar__outer {
            background-color: #f0f2f5;
            border-radius: 8px;
            height: 10px;
          }

          .el-progress-bar__inner {
            border-radius: 8px;
            transition: width 0.6s ease;
          }

          .el-progress__text {
            font-size: 13px;
            font-weight: 700;
            color: #333;
          }
        }
      }

      .stats-container {
        display: flex;
        gap: 8px;
        flex-wrap: nowrap;
        overflow: visible;

        .stat-item {
          text-align: center;
          padding: 8px 4px;
          background: #f8f9fa;
          border-radius: 4px;
          flex: 1;
          min-width: 60px;
          overflow: visible;

          .stat-number {
            display: block;
            font-size: 18px;
            font-weight: 800;
            color: #333;
            margin-bottom: 4px;
            line-height: 1;
            white-space: nowrap;

            &.completed {
              color: #67c23a;
            }

            &.abnormal {
              color: #f56c6c;
            }
          }

          .stat-label {
            font-size: 10px;
            color: #666;
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }
  }

  .point-details-section,
  .inspection-track-section {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    width: 100%;
    overflow-x: auto;

    h3 {
      margin: 0 0 20px 0;
      font-size: 16px;
      color: #333;
      padding-bottom: 10px;
    }
  }

  .inspection-track-section {
    .map-hint {
      margin: -8px 0 12px 0;
      font-size: 13px;
      color: #606266;
      line-height: 1.6;

      .legend {
        display: inline-block;
        margin-left: 10px;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 12px;
      }

      .legend--ok {
        color: #67c23a;
        border: 1px solid #c2e7b0;
        background: #f0f9eb;
      }

      .legend--bad {
        color: #f56c6c;
        border: 1px solid #fbc4c4;
        background: #fef0f0;
      }

      .legend--pending {
        color: #909399;
        border: 1px solid #d3d4d6;
        background: #f4f4f5;
      }
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    flex: 1;
    min-width: 0;

    .info-item {
      display: flex;
      flex-direction: column;
      padding: 12px 16px;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      background: #f8f9fa;
      min-height: 60px;

      .label {
        color: #666;
        font-size: 13px;
        line-height: 1.4;
        font-weight: 500;
        margin-bottom: 6px;
      }

      .value {
        color: #333;
        font-weight: 600;
        font-size: 14px;
        line-height: 1.4;
        word-break: break-all;

        &.link {
          color: #409eff;
          cursor: pointer;
          text-decoration: underline;

          &:hover {
            color: #66b1ff;
          }
        }
      }
    }
  }

  .point-card-list {
    display: flex !important;
    flex-direction: column !important;
    gap: 14px !important;
    width: 100%;
  }

  .point-card {
    display: block;
    border: 1px solid #e4e7ed !important;
    border-radius: 8px !important;
    padding: 14px !important;
    background: #fafbfc !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .point-card__header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    margin-bottom: 10px !important;
  }

  .point-card__header-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .point-card__left {
    display: flex !important;
    align-items: center !important;
    gap: 6px;
    font-size: 15px !important;
    font-weight: 600 !important;
    color: #303133 !important;
    line-height: 1.4;
  }

  .point-card__badge {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, #4f8dff 0%, #2f6bff 100%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 4px 10px rgba(47, 107, 255, 0.3);
    margin-right: 2px;
    flex-shrink: 0;
  }

  .point-card__badge i {
    font-size: 15px;
  }

  .point-card__meta-grid {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(220px, 1fr));
    gap: 8px 16px;
    margin-bottom: 12px !important;
  }

  .point-card__meta-grid .meta-item {
    display: flex;
    align-items: center;
    min-height: 24px;
  }

  .point-card__meta-grid .meta-label {
    color: #909399 !important;
    font-size: 13px !important;
    width: 88px;
    flex-shrink: 0;
  }

  .point-card__meta-grid .meta-value {
    color: #606266 !important;
    font-size: 13px !important;
    word-break: break-all;
  }

  .point-card__items-title {
    font-size: 13px !important;
    color: #606266 !important;
    margin-bottom: 8px !important;
    font-weight: 600 !important;
  }

  .point-item-list {
    width: 100%;
  }

  .point-item-inner-table .item-name {
    color: #303133 !important;
    font-size: 13px !important;
    line-height: 1.4;
  }

  .point-item-inner-table {
    width: 100%;
  }

  .point-item-inner-table ::v-deep .el-table__cell {
    padding: 6px 0;
    font-size: 12px;
  }

  .point-items-empty {
    color: #c0c4cc !important;
    font-size: 13px !important;
    padding: 8px 0;
  }

  .map-container {
    position: relative;
    height: 420px;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    overflow: hidden;
    background: #e8ecf0;

    .inspection-map-el {
      width: 100%;
      height: 100%;
      min-height: 400px;
    }

    .map-placeholder {
      height: 100%;
      min-height: 400px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: #f5f7fa;
      color: #909399;

      i {
        font-size: 48px;
        margin-bottom: 10px;
        color: #c0c4cc;
      }

      p {
        margin: 5px 0;
        font-size: 14px;
      }

      .map-placeholder-sub {
        font-size: 12px;
        color: #c0c4cc;
      }
    }
  }

  // 图片查看对话框样式
  .image-detail-content {
    .status-section {
      margin-bottom: 20px;

      .status-item {
        display: flex;
        align-items: center;
        gap: 8px;

        .status-icon {
          color: #f56c6c;
          font-size: 16px;
        }

        .status-text {
          color: #f56c6c;
          font-weight: 600;
          font-size: 16px;
        }
      }
    }

    .description-section,
    .photos-section,
    .remark-section {
      margin-bottom: 24px;

      .section-title {
        margin: 0 0 12px 0;
        font-size: 16px;
        font-weight: 600;
        color: #333;
        border-bottom: 1px solid #e8e8e8;
        padding-bottom: 8px;
      }
    }

    .description-content {
      .description-text {
        margin: 0 0 12px 0;
        line-height: 1.6;
        color: #333;
        font-size: 14px;
      }

      .report-time {
        color: #666;
        font-size: 13px;
      }
    }

    .photos-grid {
      display: block !important;
      width: 100% !important;
      overflow: hidden !important;

      .photo-item {
        float: left !important;
        width: calc(33.333% - 11px) !important;
        margin-right: 16px !important;
        margin-bottom: 16px !important;
        max-width: 200px !important;
        min-width: 150px !important;
        display: block !important;
        position: relative !important;

        &:nth-child(3n) {
          margin-right: 0 !important;
        }

        // 强制所有子元素水平排列
        * {
          float: none !important;
          display: block !important;
          width: 100% !important;
        }

        .photo-container {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          width: 100%;

          .photo-image {
            width: 100%;
            height: 150px;
            object-fit: cover;
            display: block;
          }

          .photo-timestamp {
            position: absolute;
            bottom: 8px;
            right: 8px;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
          }

          // 错误文本样式
          .image-error-text {
            width: 100% !important;
            height: 150px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            background: #f5f5f5 !important;
            color: #999 !important;
            font-size: 14px !important;
            border: 1px dashed #ddd !important;
            border-radius: 8px !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
            margin: 0 !important;
            padding: 0 !important;
            float: none !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            z-index: 1 !important;
          }
        }
      }

      // 响应式设计
      @media (max-width: 768px) {
        .photo-item {
          width: calc(50% - 8px) !important;
          margin-right: 12px !important;
          margin-bottom: 12px !important;

          &:nth-child(3n) {
            margin-right: 12px !important;
          }

          &:nth-child(2n) {
            margin-right: 0 !important;
          }
        }
      }

      @media (max-width: 480px) {
        .photo-item {
          width: 100% !important;
          margin-right: 0 !important;
          margin-bottom: 10px !important;

          &:nth-child(2n) {
            margin-right: 0 !important;
          }
        }
      }
    }

    .remark-content {
      .remark-text {
        margin: 0 0 12px 0;
        line-height: 1.6;
        color: #333;
        font-size: 14px;
      }

      .remark-info {
        color: #666;
        font-size: 13px;
      }
    }
  }

  // 对话框样式
  ::v-deep .image-dialog {
    .el-dialog__wrapper {
      z-index: 3000 !important;
    }

    .el-dialog {
      border-radius: 8px;
      z-index: 3001 !important;

      .el-dialog__header {
        background: #f8f9fa;
        border-bottom: 1px solid #e8e8e8;
        padding: 16px 20px;
        border-radius: 8px 8px 0 0;
        z-index: 3002 !important;

        .el-dialog__title {
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }
      }

      .el-dialog__body {
        padding: 20px;
        max-height: 70vh;
        overflow-y: auto;
        z-index: 3002 !important;
      }

      .el-dialog__footer {
        z-index: 3002 !important;
      }
    }
  }

  // 强制设置对话框层级
  ::v-deep .el-dialog__wrapper {
    z-index: 3000 !important;
  }

  ::v-deep .el-dialog {
    z-index: 3001 !important;
  }

  ::v-deep .el-dialog__header {
    z-index: 3002 !important;
  }

  ::v-deep .el-dialog__body {
    z-index: 3002 !important;
  }

  ::v-deep .el-dialog__footer {
    z-index: 3002 !important;
  }

  // 强制图片网格水平布局
  ::v-deep .photos-grid {
    display: block !important;
    overflow: hidden !important;
  }

  ::v-deep .photo-item {
    float: left !important;
    display: block !important;
  }

  ::v-deep .image-error-text {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    float: none !important;
    display: flex !important;
  }
}
</style>
